/**
 * État providers de paiement (Stripe + PayPal) commun aux pages /a/[slug] et /b/[slug].
 *
 * Calcule à partir du backend :
 *  - stripe / paypal disponibilité (charges_enabled)
 *  - provider sélectionné (avec fallback PayPal-only si Stripe absent)
 *  - showProviderChoice (toggle visible si les 2 connectés)
 */

import { isPaypalCardFieldsUsable } from "@donoor/checkout";

interface PaymentProvidersData {
  payment_providers?: {
    stripe?: { connected?: boolean; charges_enabled?: boolean; account_id?: string | null };
    paypal?: {
      connected?: boolean;
      charges_enabled?: boolean;
      merchant_id?: string | null;
      capabilities?: unknown;
      /** CardFields PayPal utilisables (PPCP_CUSTOM actif) — source serveur. */
      card_fields_enabled?: boolean | null;
      /**
       * Tarif PayPal appris de l'organisation (serveur : `paypal_fee_*`).
       * Indispensable pour chiffrer « couvrir les frais » au BON barème quand
       * l'acheteur paie en PayPal : sans lui, le tarif Stripe s'appliquait et
       * l'association recevait moins que le prix affiché. Karim 2026-09-22.
       */
      fee_percentage?: number;
      fee_fixed?: number;
    };
    /** Provider mis en avant par l'org (défaut "stripe"). */
    preferred_provider?: "stripe" | "paypal";
    accepts_payments?: boolean;
  };
  stripe_account_id?: string | null;
}

export function usePaymentProvidersState(source: Ref<PaymentProvidersData | null>) {
  const stripeAccountId = computed(() => source.value?.stripe_account_id || null);

  const paymentProviders = computed(() => {
    const pp = source.value?.payment_providers;
    if (pp && typeof pp === "object") return pp;
    const hasStripe = !!stripeAccountId.value;
    return {
      stripe: { connected: hasStripe, charges_enabled: hasStripe, account_id: stripeAccountId.value },
      paypal: { connected: false, charges_enabled: false, merchant_id: null, capabilities: null },
      accepts_payments: hasStripe,
    };
  });

  const stripeChargesEnabled = computed(() => paymentProviders.value.stripe?.charges_enabled ?? false);
  const paypalChargesEnabled = computed(() => paymentProviders.value.paypal?.charges_enabled ?? false);
  // Formulaire carte intégré PayPal utilisable ? (capability PPCP_CUSTOM). Sans
  // ça le SDK ne rend rien → les pages basculent la carte sur Stripe si dispo.
  const paypalCardFieldsEnabled = computed(() =>
    isPaypalCardFieldsUsable(paymentProviders.value.paypal),
  );
  const showProviderChoice = computed(() => stripeChargesEnabled.value && paypalChargesEnabled.value);
  const isPaypalOnlyMode = computed(() => !stripeChargesEnabled.value && paypalChargesEnabled.value);
  // Provider mis en avant par l'org (défaut "stripe").
  const preferredProvider = computed<"stripe" | "paypal">(() =>
    paymentProviders.value.preferred_provider === "paypal" ? "paypal" : "stripe",
  );

  // Provider sélectionné par défaut au chargement de la page : on honore le
  // choix de l'org (preferredProvider) dès lors que ce provider est utilisable,
  // sinon on prend le seul disponible. L'utilisateur peut ensuite basculer via
  // le toggle (showProviderChoice) si les 2 sont actifs.
  function resolveDefaultProvider(): "stripe" | "paypal" {
    if (isPaypalOnlyMode.value) return "paypal";
    if (!stripeChargesEnabled.value && paypalChargesEnabled.value) return "paypal";
    // Les deux actifs + préférence PayPal : on ne part sur PayPal que si ses
    // CardFields sont utilisables — sinon la carte est rendue par Stripe et le
    // provider par défaut doit suivre (le payload create-order en dépend).
    if (
      stripeChargesEnabled.value &&
      paypalChargesEnabled.value &&
      preferredProvider.value === "paypal" &&
      paypalCardFieldsEnabled.value
    ) {
      return "paypal";
    }
    return "stripe";
  }

  const selectedProvider = ref<"stripe" | "paypal">(resolveDefaultProvider());

  // `source` est asynchrone : à l'init, les providers ne sont pas encore connus
  // (selectedProvider = "stripe" par défaut). Dès qu'ils arrivent, on (re)cale
  // le défaut sur le choix de l'org — TANT QUE l'utilisateur n'a pas lui-même
  // basculé. On suit donc un flag userTouched alimenté par les pages.
  let userTouched = false;
  const markUserChoice = () => {
    userTouched = true;
  };
  watch(
    [stripeChargesEnabled, paypalChargesEnabled, preferredProvider, paypalCardFieldsEnabled],
    () => {
      if (userTouched) return;
      selectedProvider.value = resolveDefaultProvider();
    },
    { immediate: true },
  );

  return {
    stripeAccountId,
    paymentProviders,
    stripeChargesEnabled,
    paypalChargesEnabled,
    paypalCardFieldsEnabled,
    showProviderChoice,
    isPaypalOnlyMode,
    preferredProvider,
    selectedProvider,
    /** À appeler quand l'utilisateur bascule via le toggle (gèle le défaut auto). */
    markUserChoice,
  };
}
